import { formatDateUTCToString } from '@/helpers/formatStringDate';
import { parseDateUtc } from '@/helpers/parseToUTC';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Select,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { forwardRef, useEffect, useState } from 'react';
import { safeDynamic } from '@/utils/safeDynamic';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarIcon } from '../icons/Icons';

export interface DropdownOption {
  label: string;
  value: string;
}

export interface CheckboxWithDetailsFieldProps {
  id?: string;
  label?: string | React.ReactNode;
  name: string;
  options: DropdownOption[];
  error?: string;
  helpers?: FieldHelperProps<any>;
  field?: FieldInputProps<any>;
  meta?: FieldMetaProps<any>;
  isDisabled?: boolean;
  labelFontSize?: string;
  labelMb?: string;
  isRequired?: boolean;
  placeholder?: string;
  textAreaPlaceholder?: string;
  datePickerPlaceholder?: string;
  [x: string]: any;
}

interface FieldValue {
  isSelected: boolean;
  selectedOption: string;
  date: string | null;
  notes: string;
}

const DatePicker = safeDynamic<any>(() => import('react-datepicker'), {
  ssr: false,
});

export function CheckboxWithDetailsField(props: CheckboxWithDetailsFieldProps) {
  const {
    id,
    label,
    name,
    options,
    error,
    helpers,
    field,
    meta,
    isDisabled,
    labelFontSize,
    labelMb,
    isRequired,
    placeholder = 'Select an option',
    textAreaPlaceholder = 'Enter notes',
    datePickerPlaceholder = 'Choose a Specific Date',
    checkboxLabel,
    h = '60px',
    border = '3px solid',
    borderColor = 'white',
    fontSize = 'lg',
    onDark,
  } = props;

  const [fieldValue, setFieldValue] = useState<FieldValue>({
    isSelected: false,
    selectedOption: '',
    date: null,
    notes: '',
  });

  useEffect(() => {
    if (field?.value) {
      setFieldValue(field.value);
    }
  }, [field?.value]);

  const handleCheckboxChange = () => {
    if (isDisabled) return;

    const newValue = {
      ...fieldValue,
      isSelected: !fieldValue.isSelected,
    };

    setFieldValue(newValue);
    updateFormValue(newValue);
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isDisabled || !fieldValue.isSelected) return;

    const newValue = {
      ...fieldValue,
      selectedOption: e.target.value,
    };

    setFieldValue(newValue);
    updateFormValue(newValue);
  };

  const handleDateChange = (date: Date | null) => {
    if (isDisabled || !fieldValue.isSelected) return;

    const formattedDate = date ? formatDateUTCToString(date, true) : null;
    const newValue = {
      ...fieldValue,
      date: formattedDate,
    };

    setFieldValue(newValue);
    updateFormValue(newValue);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isDisabled || !fieldValue.isSelected) return;

    const newValue = {
      ...fieldValue,
      notes: e.target.value,
    };

    setFieldValue(newValue);
    updateFormValue(newValue);
  };

  const updateFormValue = (newValue: FieldValue) => {
    if (helpers) {
      helpers.setValue(newValue);
    }
  };

  const formikError = error || (meta && meta.touched && meta.error);
  const isError = !!formikError;
  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const CustomDateInput = forwardRef<HTMLDivElement, any>(({ value, onClick }, ref) => (
    <Box
      ref={ref}
      onClick={isDisabled || !fieldValue.isSelected ? undefined : onClick}
      cursor={isDisabled || !fieldValue.isSelected ? 'not-allowed' : 'pointer'}
      border={border}
      borderColor={isDisabled || !fieldValue.isSelected ? 'gray.600' : borderColor}
      borderRadius="10px"
      h={h}
      display="flex"
      alignItems="center"
      pl={5}
      color={isDisabled || !fieldValue.isSelected ? 'gray.500' : textColorPrimary}
      fontSize={fontSize}
    >
      {value || datePickerPlaceholder}
    </Box>
  ));

  return (
    <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={labelFontSize || '2xl'}
          lineHeight="normal"
          color={textColorPrimary}
          fontWeight="bold"
          mb={labelMb || 4}
          _hover={{ cursor: 'pointer' }}
        >
          {label}
        </FormLabel>
      )}

      <Flex direction="column" gap={4} mt={label ? 0 : 4}>
        <Flex direction="row" alignItems="center" gap={6} width="100%">
          <Box display="flex" alignItems="center" justifyContent="center">
            <Checkbox
              isChecked={fieldValue.isSelected}
              onChange={handleCheckboxChange}
              isDisabled={isDisabled}
              size="lg"
              colorScheme="primary"
              borderRadius="md"
            />
            {checkboxLabel && (
              <Text fontSize={fontSize} ml={2}>
                {checkboxLabel}
              </Text>
            )}
          </Box>

          <Box flex={1}>
            <Select
              value={fieldValue.selectedOption}
              onChange={handleDropdownChange}
              placeholder={placeholder}
              color={textColorPrimary}
              border={border}
              borderColor={fieldValue.isSelected && !isDisabled ? borderColor : 'gray.600'}
              borderRadius="10px"
              h={h}
              maxH={h}
              fontSize={fontSize}
              isDisabled={isDisabled || !fieldValue.isSelected}
              opacity={1}
              _disabled={{
                opacity: 0.7,
              }}
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>

        {fieldValue.isSelected && (
          <VStack spacing={6} width="100%" align="flex-start">
            <HStack width="100%">
              <CalendarIcon stroke={borderColor} w="32px" h="32px" />
              <DatePicker
                selected={fieldValue.date ? parseDateUtc(fieldValue.date) : null}
                onChange={handleDateChange}
                customInput={<CustomDateInput />}
                disabled={isDisabled || !fieldValue.isSelected}
                dateFormat="yyyy-MM-dd"
              />
            </HStack>

            <Box width="100%">
              <Textarea
                value={fieldValue.notes}
                onChange={handleNotesChange}
                placeholder={textAreaPlaceholder}
                color={textColorPrimary}
                border={border}
                borderColor={fieldValue.isSelected && !isDisabled ? borderColor : 'gray.600'}
                borderRadius="10px"
                fontSize="lg"
                resize="vertical"
                rows={5}
                isDisabled={isDisabled || !fieldValue.isSelected}
                _placeholder={{ color: textColorPrimary }}
              />
            </Box>
          </VStack>
        )}
      </Flex>

      {isError && (
        <FormErrorMessage>
          {typeof formikError === 'string' ? formikError : 'Invalid selection'}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
